export const PROJECT_PRINT_DOCUMENT_ID = "project-print-document";

const removeInteractiveAttributes = (element: Element) => {
  element.removeAttribute("contenteditable");
  element.removeAttribute("spellcheck");
  element.removeAttribute("tabindex");
};

export function removeProjectPrintDocument(ownerDocument: Document = document) {
  ownerDocument.getElementById(PROJECT_PRINT_DOCUMENT_ID)?.remove();
}

/**
 * Builds a print-only copy of the current manuscript outside the app shell.
 *
 * The project page is composed of viewport-height, independently scrolling
 * columns. Browsers only print the visible portion of those overflow regions,
 * so the manuscript is copied into normal document flow immediately before
 * the browser creates its print preview.
 */
export function createProjectPrintDocument(editorContainer: HTMLElement | null) {
  const ownerDocument = editorContainer?.ownerDocument;
  if (!editorContainer || !ownerDocument?.body) return null;

  removeProjectPrintDocument(ownerDocument);

  const sourceShell = editorContainer.closest<HTMLElement>(".editor-container-container");
  const sourceEditor = editorContainer.querySelector<HTMLElement>("#editor");
  const sourceContent = sourceEditor?.querySelector<HTMLElement>(".ProseMirror");
  if (!sourceShell || !sourceEditor || !sourceContent) return null;

  const printDocument = ownerDocument.createElement("article");
  printDocument.id = PROJECT_PRINT_DOCUMENT_ID;

  // Keep the styled-components class from the live shell so manuscript
  // typography (headings, paragraph spacing, lists, and indentation) carries
  // into the print-only copy.
  const printShell = sourceShell.cloneNode(false) as HTMLElement;
  printShell.classList.remove("focus");
  printShell.classList.add("project-print-shell");
  printShell.removeAttribute("role");
  printShell.removeAttribute("aria-label");

  const titleControl = sourceShell.querySelector<HTMLInputElement | HTMLTextAreaElement>("#title-text-wrapper textarea, #title-text-wrapper input");
  const title = titleControl?.value.trim();
  if (title) {
    const printTitle = ownerDocument.createElement("h1");
    printTitle.className = "project-print-document-title";
    printTitle.textContent = title;
    printTitle.style.fontFamily = titleControl.style.fontFamily || sourceEditor.style.fontFamily;
    printShell.append(printTitle);
  }

  const printEditorContainer = editorContainer.cloneNode(false) as HTMLElement;
  printEditorContainer.classList.add("project-print-editor-container");
  const printEditor = sourceEditor.cloneNode(false) as HTMLElement;
  const printContent = sourceContent.cloneNode(true) as HTMLElement;

  removeInteractiveAttributes(printContent);
  printContent.querySelectorAll("[contenteditable], [spellcheck], [tabindex]").forEach(removeInteractiveAttributes);

  printEditor.append(printContent);
  printEditorContainer.append(printEditor);
  printShell.append(printEditorContainer);
  printDocument.append(printShell);
  ownerDocument.body.append(printDocument);

  return printDocument;
}
